(async () => {
    const { Core } = await importModule('Core');

    const LS_KEY              = 'languageRedirectDecision'; // 'dismissed' | 'accepted'
    const ATTR_CURRENT_LOCALE = 'current-locale';
    const ATTR_AVAILABLE      = 'available-locales';
    const ATTR_POSITION       = 'popup-position';
    const ATTR_DELAY          = 'display-delay';
    const ATTR_SCROLL         = 'scroll-trigger';
    const DEFAULT_POSITION    = 'center';

    customElements.define('language-redirect-popup', class extends Core {

        async render() {
            await customElements.whenDefined('modal-popup');
            this._initPopup();
        }

        _initPopup() {
            // User already made a decision — respect it permanently.
            if (localStorage.getItem(LS_KEY)) return;

            const currentLocale     = this.getAttribute(ATTR_CURRENT_LOCALE);
            const availableLocales  = this.getAttribute(ATTR_AVAILABLE).split(',').map(l => l.trim());
            const browserLocale     = (navigator.language || 'en').split('-')[0].toLowerCase();

            // Nothing to do: already on the right language.
            if (browserLocale === currentLocale) return;

            // Browser language not offered by this store — fall back to English.
            if (!availableLocales.includes(browserLocale)) {
                if (currentLocale === 'en' || !availableLocales.includes('en') || !this._getCopy('en')) return;
                this._targetLocale = 'en';
                this._useFallbackMessage = true;
                this._scheduleTrigger();
                return;
            }

            // No copy defined for this locale — bail silently.
            if (!this._getCopy(browserLocale)) return;

            this._targetLocale = browserLocale;
            this._scheduleTrigger();
        }

        // Déclenche le popup une seule fois.
        // - Seuil de scroll défini : le popup n'apparaît QUE lorsque
        //   l'utilisateur a défilé ce % de la page (le héros a fini de peindre,
        //   donc aucun impact sur le LCP). Un visiteur qui ne défile pas ne le
        //   voit pas — il garde le sélecteur de langue de l'en-tête.
        // - Sinon : comportement d'origine (délai temporel seul).
        _scheduleTrigger() {
            const scrollPct = +this.getAttribute(ATTR_SCROLL) || 0;

            if (scrollPct <= 0) {
                setTimeout(() => this._triggerModal(), this.displayDelay);
                return;
            }

            const onScroll = () => {
                if (this._fired) return;
                const scrollable = document.documentElement.scrollHeight - window.innerHeight;
                const reached = scrollable <= 0
                    ? false
                    : (window.scrollY / scrollable) * 100 >= scrollPct;
                if (!reached) return;
                this._fired = true;
                window.removeEventListener('scroll', onScroll);
                this._triggerModal();
            };

            window.addEventListener('scroll', onScroll, { passive: true });
            onScroll(); // couvre le cas d'une page déjà défilée au chargement
        }

        _getCopy(locale) {
            const message = this.getAttribute(`copy-${locale}-message`);
            const accept  = this.getAttribute(`copy-${locale}-accept`);
            const dismiss = this.getAttribute(`copy-${locale}-dismiss`);
            if (!message || !accept || !dismiss) return null;
            return { message, accept, dismiss };
        }

        _triggerModal() {
            const copy     = this._getCopy(this._targetLocale);
            const template = document.getElementById('languageRedirectPopup');
            if (!template || !copy) return;

            // Fill placeholder elements inside the <template> before it is cloned.
            const message = this._useFallbackMessage
                ? (this.getAttribute('copy-en-fallback-message') || copy.message)
                : copy.message;
            template.content.querySelector('[data-redirect-message]').textContent = message;
            template.content.querySelector('[data-redirect-accept]').textContent  = copy.accept;
            template.content.querySelector('[data-redirect-dismiss]').textContent = copy.dismiss;
            template.content.querySelector('[data-redirect-locale]').value        = this._targetLocale;

            this.publish('modal:open', {
                target:       '#languageRedirectPopup',
                backdrop:     true,
                close:        false,
                animation:    'fade',
                transition:   400,
                unlockScreen: false,
                position:     this.popupPosition
            });

            // Bind buttons after the modal has cloned and inserted the template.
            setTimeout(() => this._bindButtons(), 150);
        }

        _bindButtons() {
            const acceptBtn  = document.querySelector('[data-redirect-accept]');
            const dismissBtn = document.querySelector('[data-redirect-dismiss]');
            const closeBtn   = document.querySelector('[data-redirect-close]');
            const form       = document.querySelector('[data-redirect-form]');

            acceptBtn?.addEventListener('click', () => {
                localStorage.setItem(LS_KEY, 'accepted');
                form?.submit();
            });

            const dismiss = () => {
                localStorage.setItem(LS_KEY, 'dismissed');
                this.publish('modal:close');
            };

            dismissBtn?.addEventListener('click', dismiss);
            // Fermer via le X équivaut à « rester sur la langue actuelle » :
            // on mémorise la décision pour ne plus reproposer.
            closeBtn?.addEventListener('click', dismiss);
        }

        get displayDelay() {
            return this.getAttribute(ATTR_DELAY) * 1000;
        }

        get popupPosition() {
            return window.innerWidth <= 992
                ? DEFAULT_POSITION
                : (this.getAttribute(ATTR_POSITION) || DEFAULT_POSITION);
        }
    });
})();
